import { defineStore } from "pinia";
import { ref } from "vue";
import SectionService from "@/services/hrm/sectionService";
import { toast } from "@/utils/toast";
import { createScopedCache } from "./_cache";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

const sectionListCache = createScopedCache("sections:list");
const SECTIONS_LIST_TTL = 5 * 60 * 1000;

export const useSectionStore = defineStore("section", () => {
  const sections = ref([]);
  const loading = ref(false);
  const error = ref(null);

  async function fetchSections(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_EMPLOYEE))) {
      sections.value = [];
      return;
    }

    loading.value = true;
    error.value = null;
    const { force, ...query } = params;
    try {
      await sectionListCache.get(query, {
        ttlMs: SECTIONS_LIST_TTL,
        force: force === true,
        loader: async () => {
          const response = await SectionService.getAll(query);
          return { sections: response.data || [] };
        },
        onHydrate: ({ sections: rows }) => {
          sections.value = rows || [];
        },
      });
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to fetch sections";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function ensureSections(params = {}) {
    return await fetchSections({ ...params, force: false });
  }

  async function createSection(sectionData) {
    loading.value = true;
    error.value = null;
    try {
      const response = await SectionService.create(sectionData);
      sectionListCache.clear();
      sections.value.push(response.data);
      toast("Section created successfully!");
      return response.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to create section";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateSection(sectionData) {
    loading.value = true;
    error.value = null;
    try {
      const response = await SectionService.update(sectionData.id, sectionData);
      sectionListCache.clear();
      const index = sections.value.findIndex((s) => s.id === sectionData.id);
      if (index !== -1) {
        sections.value.splice(index, 1, response.data);
      }
      toast("Section updated successfully!");
      return response.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to update section";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteSections(sectionIds) {
    loading.value = true;
    error.value = null;
    try {
      await Promise.all(sectionIds.map((id) => SectionService.delete(id)));
      sectionListCache.clear();
      sections.value = sections.value.filter((s) => !sectionIds.includes(s.id));
      toast("Sections deleted successfully!");
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to delete sections";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    sections,
    loading,
    error,
    fetchSections,
    ensureSections,
    createSection,
    updateSection,
    deleteSections,
  };
});
